/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { Colors, Margin, Spacer, Text } from '@zapier/design-system';
import { isolateComponent } from '@zapier/common-utils';
import { ActiveOfferBanner } from '@zapier/promotions';

import { PendingPaymentWarningBar } from '@zapier/design-system-revenue/src/components/other';
import { useAccountSwitch } from '@zapier/design-system-revenue/src/hooks';

import {
  ChangeType,
  PlanStack,
  PlanV2,
} from '@zapier/design-system-revenue/src/types';

import { CurrencyConversionWarningBar } from './CurrencyConversionWarningBar';
import { CurrencyChargeWarningBar } from './CurrencyChargeWarningBar';
import { Faq } from './Faq';
import { Hero } from './Hero';
import { LegacyPlanInfoBar } from './LegacyPlanInfoBar';
import { LegacyPlanModal } from './LegacyPlanModal';
import { PlanCardCollection } from './PlanCardCollection';
import { scrollTo } from './scrollTo';
import { usePricingPage } from './usePricingPage';

type PricingPageV3Props = {
  currentPlan?: PlanV2;
  heroImageUrl: { [x: string]: string };
  isOnLegacyPlan: boolean;
  onSelectPlan: (selectedPlan: PlanV2, planChangeType: ChangeType) => void;
  plans: PlanStack[];
};

const Styles = {
  root: css`
    background-color: ${Colors.white};
    display: flex;
    justify-content: center;
    overflow-x: hidden; /* prevents horizontal scroll bar from hero image */
  `,

  divider: css`
    border-top: 3px solid ${Colors.grey100};
  `,

  wrapper: css`
    padding: 0 20px;
    width: 100%;
    max-width: 1240px;
  `,
};

export const PricingPageV3 = ({
  currentPlan,
  heroImageUrl,
  isOnLegacyPlan,
  onSelectPlan,
  plans,
}: PricingPageV3Props) => {
  const {
    activeOffer,
    activeOfferEndDate,
    featureListRef,
    isLoading,
    isShowingLegacyPlanModal,
    pendingInvoice,
    selectedCurrency,
    setIsShowingLegacyPlanModal,
    size,
  } = usePricingPage();
  const {
    isSwitchOn: multiCurrencyEnabled,
    isLoading: isLoadingSwitch,
  } = useAccountSwitch('billingMulticurrencySupport');

  return (
    <div css={Styles.root}>
      <div css={Styles.wrapper}>
        <Hero
          buttonOnClick={() => scrollTo(featureListRef.current, true)}
          buttonText="Compare plans"
          description="Whether your time-saving automation needs are large or small, we’re here to help you scale."
          imageUrl={heroImageUrl}
          size={size}
          title="Plans &amp; Pricing"
        />

        <Spacer height={20} />

        {!isLoading && !activeOffer && currentPlan && isOnLegacyPlan && (
          <Margin margin="0 0 10px 0">
            <LegacyPlanInfoBar toggleModal={setIsShowingLegacyPlanModal} />
          </Margin>
        )}

        {!isLoading &&
          activeOffer &&
          !activeOffer?.variant?.isControl &&
          currentPlan && (
            <ActiveOfferBanner
              currency={selectedCurrency}
              currentPlan={currentPlan}
              margin="0 0 10px 0"
              offer={activeOffer}
              offerEndDate={activeOfferEndDate!}
            />
          )}

        {pendingInvoice && (
          <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
        )}

        {!isLoading && selectedCurrency?.name !== 'USD' ? (
          multiCurrencyEnabled && !isLoadingSwitch ? (
            <CurrencyChargeWarningBar
              margin="0 0 10px 0"
              selectedCurrencyCode={selectedCurrency.name}
            />
          ) : (
            <CurrencyConversionWarningBar margin="0 0 10px 0" />
          )
        ) : null}

        {size === 'small' && (
          <Margin margin="20px 0 10px 0">
            <Text tag="h2" type={'subtitle800'}>
              Compare Plans
            </Text>
          </Margin>
        )}

        <PlanCardCollection
          currency={selectedCurrency}
          currentPlan={currentPlan}
          isOnLegacyPlan={isOnLegacyPlan}
          onSelect={onSelectPlan}
          plans={plans}
          scrollTargetRef={featureListRef}
          size={size}
        />

        <Margin margin="30px 0 40px 0">
          <hr css={Styles.divider} />
        </Margin>

        <Faq />

        <Spacer height={40} />
      </div>

      {currentPlan && isShowingLegacyPlanModal && (
        <LegacyPlanModal
          currency={selectedCurrency}
          onClose={setIsShowingLegacyPlanModal}
          plan={currentPlan}
        />
      )}
    </div>
  );
};
